import React, { useEffect, useState } from "react";
import api, { formatSeconds } from "../lib/api";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const AXIS_TICK = { fill: "hsl(var(--muted-foreground))", fontSize: 11 };
const TOOLTIP_STYLE = { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 };

const RANGE_OPTIONS = [7, 30, 90];
const DEFAULT_RANGE_DAYS = 30;

export default function Reports() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState(DEFAULT_RANGE_DAYS);

  useEffect(() => {
    api.get(`/reports?days=${range}`).then((r) => setData(r.data));
  }, [range]);

  const daily = (data?.daily || []).map((d) => ({
    label: new Date(d.date).toLocaleDateString("en", { day: "numeric", month: "short" }),
    minutes: Math.round(d.seconds / 60),
  }));

  const pie = (data?.subjects || []).filter((s) => s.seconds > 0).map((s) => ({
    name: s.name, value: s.seconds, color: s.color,
  }));

  const total = (data?.daily || []).reduce((a, b) => a + b.seconds, 0);

  return (
    <div className="space-y-6" data-testid="reports-root">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">Reports</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Your study story.</h1>
          <p className="text-zinc-600 dark:text-zinc-400">Data is the antidote to wishful thinking.</p>
        </div>
        <div className="flex gap-1 rounded-lg border border-zinc-200 p-1 dark:border-zinc-800">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              data-testid={`range-${r}`}
              className={`rounded px-3 py-1.5 text-xs font-medium ${range === r ? "bg-emerald-500 text-white" : "text-zinc-600 dark:text-zinc-300"}`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card label="Total time" value={formatSeconds(total)} testid="report-total" />
        <Card label="Active days" value={`${data?.active_days || 0} / ${range}`} testid="report-active" />
        <Card label="Consistency" value={`${Math.round(data?.consistency || 0)}%`} testid="report-consistency" />
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900" data-testid="daily-chart">
        <h3 className="font-heading text-lg font-semibold">Daily study (minutes)</h3>
        <div className="mt-4 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false}/>
              <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false}/>
              <Tooltip contentStyle={TOOLTIP_STYLE}/>
              <Line type="monotone" dataKey="minutes" stroke="#10B981" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900" data-testid="subjects-pie">
          <h3 className="font-heading text-lg font-semibold">By subject</h3>
          {pie.length === 0 ? (
            <div className="py-12 text-center text-sm text-zinc-500">Tag your sessions with subjects to see this breakdown.</div>
          ) : (
            <div className="mt-2 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pie} dataKey="value" nameKey="name" outerRadius={90} innerRadius={50}>
                    {pie.map((p) => <Cell key={p.name} fill={p.color}/>)}
                  </Pie>
                  <Legend />
                  <Tooltip formatter={(v) => formatSeconds(v)} contentStyle={TOOLTIP_STYLE}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900" data-testid="subject-list">
          <h3 className="font-heading text-lg font-semibold">Per-subject time</h3>
          <div className="mt-4 space-y-3">
            {(data?.subjects || []).map((s) => (
              <div key={s.subject_id} className="flex items-center gap-3">
                <span className="h-3 w-3 rounded-full" style={{ background: s.color }}/>
                <span className="flex-1 text-sm">{s.name}</span>
                <span className="text-sm font-medium tabular-nums">{formatSeconds(s.seconds)}</span>
              </div>
            ))}
            {(data?.subjects?.length || 0) === 0 && (
              <div className="text-sm text-zinc-500">No subjects yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Card = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900" data-testid={testid}>
    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{label}</div>
    <div className="mt-2 font-heading text-2xl font-bold">{value}</div>
  </div>
);
